import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators} from '@angular/forms';
import { CamionesService } from './../../../services/API/camiones/camiones.service';
import { MaterialesService } from './../../../services/API/materiales/materiales.service';
import { CargasService } from './../../../services/API/cargas/cargas.service';
import { io } from 'socket.io-client';
import { AdviceComponent } from './components/advice/advice.component';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from './../../../services/API/auth/auth.service';
import { ScalesService } from './../../../services/API/scales/scales.service';
import { AuxiliarService } from './../../../services/auxiliar.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

const LAST_SCALE_KEY = 'retsl_last_scale_id';

type SavingPhase = 'idle' | 'capturing' | 'saving';

@Component({
    selector: 'app-carga',
    templateUrl: './carga.component.html',
    styleUrls: ['./carga.component.css'],
    standalone: false
})
export class CargaComponent implements OnInit, OnDestroy {

  socket;

  listsLoading = true;
  isSaving = false;
  savingPhase: SavingPhase = 'idle';

  form: UntypedFormGroup;
  camiones: any = [];
  materiales: any = [];
  scales: any = [];

  loadErrors = {
    scales: '',
    camiones: '',
    materiales: '',
  };

  pesoBascula: number = 0;
  pesoBasculaChanged: boolean = false;
  colorBascula: string = 'rgb(55, 255, 0)';
  heightBascula: string = '50px';
  widthBascula: string = '25px';
  disabledColorBascula: string = 'rgb(0, 0, 0)';
  maxNumberLengthBascula: number = 10;

  idToken: string = '';

  timeOut: any = null;
  checkInterval: any = null;
  private helloListener: ((msg: { scale?: string | number; scale_id?: string | number }) => void) | null = null;

  socketConnected = false;
  basculaConnection = false;

  constructor(
    private dialog: MatDialog,
    public authService: AuthService,
    private fb: UntypedFormBuilder,
    private camionesService: CamionesService,
    private materialesService: MaterialesService,
    private cargasService: CargasService,
    private scalesService: ScalesService,
    private auxiliarService: AuxiliarService,
  ) { }

  ngOnInit(): void {
    this.userRolCheck('permiso_pesar');
    this.form = this.fb.group({
      matricula: [null, [Validators.required]],
      nAlbaran: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(256), Validators.pattern('^[0-9]*$')]],
      material: [null, [Validators.required]],
      observaciones: ['', [Validators.maxLength(512)]],
      scaleId: [null, [Validators.required]],
    });
    this.setupSocketConnection();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    if (this.helloListener && this.socket) {
      this.socket.off('HelloControlResponse', this.helloListener);
    }
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  isSaveDisabled(): boolean {
    return this.getSaveDisabledReason() !== '';
  }

  getSaveDisabledReason(): string {
    if (this.listsLoading) {
      return 'Cargando datos…';
    }
    if (this.isSaving) {
      if (this.savingPhase === 'capturing') {
        return 'Capturando imagen…';
      }
      if (this.savingPhase === 'saving') {
        return 'Guardando pesaje…';
      }
      return 'Guardando…';
    }
    if (this.loadErrors.scales) {
      return 'No se pudieron cargar las básculas';
    }
    if (this.loadErrors.camiones) {
      return 'No se pudieron cargar los camiones';
    }
    if (this.loadErrors.materiales) {
      return 'No se pudieron cargar los materiales';
    }
    if (!this.socketConnected) {
      return 'Sin conexión con el servidor socket';
    }
    if (!this.basculaConnection) {
      return `Sin conexión con ${this.selectedScaleLabel()}`;
    }
    if (!this.form?.valid) {
      return 'Complete los campos obligatorios';
    }
    return '';
  }

  userRolCheck(permission: string): void {
    if (!this.authService.userRolCheck(permission)) {
      this.authService.logout();
    }
  }

  setupSocketConnection(): void {
    this.socket = io(this.auxiliarService.getSocket());
    this.socket.on('connect', () => {
      this.socketConnected = true;
      this.emitCheck();
    });
    this.socket.on('disconnect', () => {
      this.socketConnected = false;
      this.basculaConnection = false;
    });
    this.reciveSockets();
    this.checkBasculaConnection();
  }

  reciveSockets(): void {
    this.socket.on('captureImageResponse', (msg) => {
      if (msg['idToken'] !== this.idToken) {
        return;
      }
      clearTimeout(this.timeOut);
      const { value } = this.form;
      const imageName = (msg['imageName'] ?? '').toString().trim();
      if (!imageName) {
        this.logPesajeError('captureImageResponse sin imageName', msg);
        this.finishSaving();
        this.showDialogAdvice('error');
        return;
      }
      this.savingPhase = 'saving';
      this.cargasService.newCarga(
        value['matricula'].toString(),
        value['nAlbaran'].toString(),
        value['material'].toString(),
        this.pesoBascula.toString(),
        value['observaciones'] ?? '',
        imageName,
        value['scaleId'].toString()
      ).subscribe({
        next: () => {
          this.finishSaving();
          this.showDialogAdvice('correct');
        },
        error: (err) => {
          this.logPesajeError('newCarga', err);
          this.finishSaving();
          this.showDialogAdvice('error');
        }
      });
    });

    this.socket.on('captureImageError', (msg) => {
      if (msg['idToken'] !== this.idToken) {
        return;
      }
      clearTimeout(this.timeOut);
      this.logPesajeError('captureImageError', msg);
      this.finishSaving();
      this.showDialogAdvice('error');
    });

    this.socket.on('sendPeso', (pesoRecived) => {
      const currentScale = String(this.form?.get('scaleId')?.value ?? '');
      if (pesoRecived && String(pesoRecived['scale'] ?? '') === currentScale) {
        this.pesoBascula = Number.parseFloat(String(pesoRecived['peso']));
        if (Number.isNaN(this.pesoBascula)) {
          return;
        }
        this.pesoBasculaChanged = true;
        this.basculaConnection = true;
      }
    });
  }

  checkBasculaConnection(): void {
    this.basculaConnection = false;
    this.emitCheck();
    this.checkInterval = setInterval(() => {
      this.emitCheck();
    }, 10000);
  }

  emitCheck(): void {
    const scaleId = String(this.form?.get('scaleId')?.value ?? '');
    if (!scaleId || !this.socket?.connected) {
      this.basculaConnection = false;
      return;
    }

    this.socket.emit('joinScale', { scale: scaleId });

    if (this.helloListener) {
      this.socket.off('HelloControlResponse', this.helloListener);
      this.helloListener = null;
    }

    let resolved = false;
    this.helloListener = (msg) => {
      const msgScale = String(msg?.scale ?? msg?.scale_id ?? '');
      if (msgScale !== scaleId) {
        return;
      }
      resolved = true;
      this.basculaConnection = true;
    };
    this.socket.on('HelloControlResponse', this.helloListener);
    this.socket.emit('HelloControl', { msg: 'Hello', scale: scaleId });

    setTimeout(() => {
      if (!resolved) {
        this.basculaConnection = false;
      }
      if (this.helloListener && this.socket) {
        this.socket.off('HelloControlResponse', this.helloListener);
        this.helloListener = null;
      }
    }, 3000);
  }

  onScaleChange(): void {
    const id = this.form.get('scaleId')?.value;
    if (id == null) {
      return;
    }
    localStorage.setItem(LAST_SCALE_KEY, String(id));
    this.socket.emit('joinScale', { scale: id });
    this.resetFormForScale(id);
    this.emitCheck();
  }

  selectedScaleLabel(): string {
    const id = this.form?.get('scaleId')?.value;
    if (id == null) {
      return 'la báscula seleccionada';
    }
    const scale = this.scales.find((s) => s.id === id);
    if (scale) {
      return scale.name;
    }
    return 'la báscula seleccionada';
  }

  saveData(): void {
    if (this.isSaving || this.isSaveDisabled()) {
      if (!this.form.valid) {
        this.form.markAllAsTouched();
      }
      return;
    }
    const { value, valid } = this.form;
    if (!valid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.savingPhase = 'capturing';
    const token = this.makeid(10);
    this.idToken = token;
    this.socket.emit('joinScale', { scale: value['scaleId'] });
    this.socket.emit('captureImage', { idToken: token, scale: value['scaleId'] });
    this.waitError();
  }

  private finishSaving(): void {
    this.idToken = '';
    this.isSaving = false;
    this.savingPhase = 'idle';
  }

  waitError(): void {
    this.timeOut = setTimeout(() => {
      this.logPesajeError('captureImage timeout', {
        scaleId: this.form?.get('scaleId')?.value,
        idToken: this.idToken,
      });
      this.finishSaving();
      this.showDialogAdvice('error');
    }, 20000);
  }

  private logPesajeError(context: string, detail: unknown): void {
    if (detail && typeof detail === 'object' && detail !== null && 'error' in detail) {
      const httpErr = detail as { status?: number; statusText?: string; error?: unknown; message?: string };
      console.error('[pesaje]', context, {
        status: httpErr.status,
        statusText: httpErr.statusText,
        message: httpErr.message,
        body: httpErr.error,
      });
      return;
    }
    console.error('[pesaje]', context, detail);
  }

  makeid(length: number): string {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  loadInitialData(): void {
    this.listsLoading = true;
    this.loadErrors = { scales: '', camiones: '', materiales: '' };

    forkJoin({
      scales: this.scalesService.getAllowedScales('true').pipe(
        catchError((err) => {
          this.loadErrors.scales = 'No se pudieron cargar las básculas.';
          this.logPesajeError('getScales', err);
          return of([]);
        })
      ),
      camiones: this.camionesService.getCamiones('true').pipe(
        catchError((err) => {
          this.loadErrors.camiones = 'No se pudieron cargar los camiones.';
          this.logPesajeError('getCamiones', err);
          return of([]);
        })
      ),
      materiales: this.materialesService.getMateriales('Pesaje').pipe(
        catchError((err) => {
          this.loadErrors.materiales = 'No se pudieron cargar los materiales.';
          this.logPesajeError('getMateriales', err);
          return of([]);
        })
      ),
    }).subscribe(({ scales, camiones, materiales }) => {
      this.scales = scales || [];
      this.camiones = camiones || [];
      this.materiales = materiales || [];
      this.listsLoading = false;

      if (this.scales.length === 0) {
        return;
      }

      const saved = localStorage.getItem(LAST_SCALE_KEY);
      const savedId = saved ? parseInt(saved, 10) : NaN;
      const defaultScale = !isNaN(savedId) && this.scales.some((s) => s.id === savedId)
        ? savedId
        : this.scales[0].id;

      this.form.get('scaleId')?.setValue(defaultScale);
      this.socket?.emit('joinScale', { scale: defaultScale });
      this.applyScaleDefaults(defaultScale);
      this.emitCheck();
    });
  }

  private resetFormForScale(scaleId: number): void {
    this.form.reset({
      scaleId,
      matricula: null,
      material: null,
      nAlbaran: '',
      observaciones: '',
    });
    this.pesoBascula = 0;
    this.pesoBasculaChanged = false;
    this.applyScaleDefaults(scaleId);
  }

  private applyScaleDefaults(scaleId: number): void {
    const scale = this.scales.find((s) => s.id === scaleId);
    if (!scale) {
      return;
    }

    const patch: { matricula?: number; material?: number } = {};
    const defaultCamionId = this.parseNullableId(scale.default_id_camion);
    const defaultMaterialId = this.parseNullableId(scale.default_id_material);

    if (defaultCamionId != null && this.camiones.some((c) => c.id === defaultCamionId)) {
      patch.matricula = defaultCamionId;
    }
    if (defaultMaterialId != null && this.materiales.some((m) => m.id === defaultMaterialId)) {
      patch.material = defaultMaterialId;
    }

    if (Object.keys(patch).length > 0) {
      this.form.patchValue(patch);
    }
  }

  private parseNullableId(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = parseInt(String(value), 10);
    return isNaN(parsed) ? null : parsed;
  }

  showDialogAdvice(type: string = 'error'): void {
    this.dialog.open(AdviceComponent, {
      data: { type },
      width: '90%',
      maxWidth: '350px',
      panelClass: 'advice-dialog-panel',
      autoFocus: false
    });
  }

}
